import { supabase } from '../../lib/supabase';
import type { ThreadMessage, ThreadStatus } from '../../shared/messages/types';

/**
 * Phase 179a — the student half of threads.
 *
 * Internal staff notes never reach here. They are filtered by the RPC AND by
 * the SELECT policy behind it, so this file has no notion of visibility at
 * all — which is the point: there is nothing here to accidentally render.
 */
interface UnknownRpc {
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message: string } | null }>;
}
const rpcUnknown = supabase as unknown as UnknownRpc;

async function call<T>(fn: string, args: Record<string, unknown>, fallback: T): Promise<T> {
  const { data, error } = await rpcUnknown.rpc(fn, args);
  if (error !== null) throw new Error(error.message);
  return (data ?? fallback) as T;
}

export interface StudentThreadRow {
  id: string;
  subject: string;
  status: ThreadStatus;
  last_message_at: string;
  last_author_role: 'staff' | 'student';
  preview: string | null;
  batch_name: string | null;
  is_announcement: boolean;
  unread: number;
  created_at: string;
}

export interface StudentThreadDetail {
  thread: {
    id: string;
    subject: string;
    status: ThreadStatus;
    is_announcement: boolean;
    created_at: string;
  } | null;
  messages: ThreadMessage[];
}

export const STUDENT_MESSAGE_KEYS = {
  list: ['student', 'threads'] as const,
  detail: (threadId: string) => ['student', 'thread', threadId] as const,
  unread: ['student', 'threads', 'unread'] as const,
};

export async function listMyThreads(): Promise<StudentThreadRow[]> {
  return call<StudentThreadRow[]>('student_list_threads', { p_limit: 100 }, []);
}

export async function getMyThread(threadId: string): Promise<StudentThreadDetail> {
  return call<StudentThreadDetail>(
    'student_thread_detail',
    { p_thread_id: threadId },
    { thread: null, messages: [] },
  );
}

export async function askQuestion(body: string): Promise<string> {
  return call<string>('student_start_thread', { p_body: body }, '');
}

export async function replyToMyThread(threadId: string, body: string): Promise<void> {
  await call<unknown>('student_reply_thread', { p_thread_id: threadId, p_body: body }, null);
}

export async function markMyThreadRead(threadId: string): Promise<void> {
  await call<unknown>('mark_thread_read', { p_thread_id: threadId }, null);
}

/** The nav badge. One number, no thread list. */
export async function getUnreadMessageCount(): Promise<number> {
  return call<number>('student_unread_message_count', {}, 0);
}
